import { Check, RotateCcw, Send, X, Camera, Layers, Wifi } from 'lucide-react';
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { connectionManager } from '../services/connectionManager';
import { diceRegistryService } from '../services/diceRegistryService';
import { DiscordClient } from '../services/discordClient';
import { WebSocketClient } from '../services/websocket';
import { blobToBase64 } from '@/utils/blob';
import {
  recognizeDice,
  recognizeMultipleDice,
  RecognitionResult,
  DiceSpec,
  DetectedDie
} from '../services/recognition';
import { sessionToEndpoints } from '../services/sessionAdapter';
import { extractFirstFrame } from '../services/videoFrameExtractor';
import { useVideoRollCapture } from '@/hooks/useVideoRollCapture';
import type { RollVideoRecording } from '@/services/rollVideoRecorder';
import { useActiveConnectionsStore } from '../stores/activeConnectionsStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { useSessionStore } from '../stores/sessionStore';
import { useSoulBondsStore } from '@/stores/soulBondsStore';
import { CameraView, CameraViewHandle, TorchButton, type TorchState } from '@/components/CameraView';
import { CameraTopBar } from '@/components/CameraTopBar';
import { EndpointTypeIcon } from '@/components/EndpointTypeIcon';
import { FrogMascot } from '@/components/FrogMascot';
import { IosVideoWarning } from '@/components/IosVideoWarning';
import { PhotoHeroBatchResult } from '@/components/PhotoHeroBatchResult';
import { PhotoHeroResult } from '@/components/PhotoHeroResult';
import { PwaInstallNudge } from '@/components/PwaInstallNudge';
import { FrogWaitAnimation } from '@/components/FrogWaitAnimation';
import { FloatingCaptureButton } from '@/components/registration';
import { AiRecognitionIndicator } from '@/components/AiRecognitionIndicator';
import { RollSettingsDrawer } from '@/components/RollSettingsDrawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { cn } from '@/lib/utils';

const SERVICE_TAG = '[ROLL]';

type RollState = 'waiting' | 'ready' | 'capturing' | 'reviewing' | 'submitting';
type CaptureMode = 'sequential' | 'batch';

interface CapturedDie {
  type: string;
  /** Still image data URL. For `captureKind === 'video'`, this is the extracted first frame. */
  imageData: string;
  /** Recorded video blob (TICKET-281c). Present only when `captureKind === 'video'`. */
  videoBlob?: Blob;
  /** Whether this die was captured as a still image or a short video. */
  captureKind: 'image' | 'video';
  result: RecognitionResult | null;
  isProcessing: boolean;
  wasEdited?: boolean;
  /** The original recognised value before the first manual edit */
  originalValue?: number;
}

interface BatchResult {
  dice: (DetectedDie & { wasEdited?: boolean; originalValue?: number })[];
  imageData: string;
  warnings?: string[];
}

export function RollPage() {
  const isLandscape = useIsLandscape();
  const {
    connectionStatus,
    serverUrl,
    sessionCode,
    pendingRollRequest,
    playerId,
    setPendingRollRequest
  } = useSessionStore();

  // Derive endpoints from sessions store via session adapter
  const { sessions } = useSessionsStore();
  const { connections, getActiveCount } = useActiveConnectionsStore();

  /** Derive the full endpoint list from all saved sessions */
  const allEndpoints = useMemo(() => {
    return sessions.flatMap((s) => sessionToEndpoints(s));
  }, [sessions]);

  /**
   * Get connected endpoints from BOTH connection systems for backward compatibility.
   *
   * 1. **New architecture** (preferred): activeConnectionsStore (ephemeral, multi-connection)
   * 2. **Old architecture** (fallback): sessionStore (persistent, single-connection)
   */
  const connectedEndpoints = allEndpoints.filter(ep => {
    const state = connections.get(ep.id);
    if (state?.status === 'connected') {
      return true;
    }
    // Fallback: Check sessionStore for backward compatibility (E2E tests, old architecture)
    if (connectionStatus === 'connected' && ep.credentials) {
      const foundryCredentials = ep.credentials as { serverUrl?: string; sessionCode?: string };
      return foundryCredentials.serverUrl === serverUrl && foundryCredentials.sessionCode === sessionCode;
    }
    return false;
  });

  // Track selected endpoint for sending rolls
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null);

  // Auto-select first connected endpoint if none selected
  useEffect(() => {
    if (!selectedEndpointId && connectedEndpoints.length > 0) {
      setSelectedEndpointId(connectedEndpoints[0].id);
    }
  }, [selectedEndpointId, connectedEndpoints]);

  const cameraRef = useRef<CameraViewHandle>(null);
  const [rollState, setRollState] = useState<RollState>('waiting');
  const [captureMode, setCaptureMode] = useState<CaptureMode>('sequential');
  const [capturedDice, setCapturedDice] = useState<CapturedDie[]>([]);
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null);
  const [currentDieIndex, setCurrentDieIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [torchState, setTorchState] = useState<TorchState>({ supported: false, enabled: false });

  // Video recording state (TICKET-290 motion-driven).
  const videoCapture = useVideoRollCapture();

  /** Stable callback to avoid restarting camera on every parent re-render */
  const handleCameraError = useCallback((err: Error) => setError(err.message), []);

  const activeCount = getActiveCount();
  /**
   * Connection status from either architecture:
   * - Old: sessionStore.connectionStatus === 'connected'
   * - New: activeConnectionsStore has activeCount > 0
   *
   * Precedence: Either system can establish connected state (OR logic)
   */
  const isConnected = connectionStatus === 'connected' || activeCount > 0;
  const { appSettings, updateSettings } = useSoulBondsStore();
  // Backend silently downgrades gated models for guests, so no client-side gating is needed.
  const aiModel = appSettings.aiRecognitionModel ?? 'gpt-4.1-mini';
  // TICKET-299: persistent capture-mode preference (photo vs video).
  const persistedCaptureMode = appSettings.captureMode ?? 'photo';
  const hasRequest = pendingRollRequest !== null;
  const totalDiceNeeded = pendingRollRequest?.dice.reduce((sum, d) => sum + d.count, 0) || 0;

  // Proof-mode plumbing (TICKET-281c). Default to 'either' when GM omits the field.
  const proofMode = pendingRollRequest?.proofMode ?? 'either';
  const canVideo = proofMode === 'video' || proofMode === 'either' || proofMode === 'none';
  const requiresVideo = proofMode === 'video';

  // TICKET-299: GM-imposed lock derived from proofMode. When set, the drawer toggle is
  // shown as the locked value (overriding persisted preference) and disabled.
  //   'image' → 'photo' lock, 'video' → 'video' lock, otherwise undefined (free choice).
  const lockedMode: 'photo' | 'video' | undefined =
    proofMode === 'image' ? 'photo' : proofMode === 'video' ? 'video' : undefined;
  const effectiveCaptureMode: 'photo' | 'video' = lockedMode ?? persistedCaptureMode;

  // Batch capture is incompatible with required-video (TICKET-281c §3.6) — only one
  // S3 video key per requestId, and batch is fundamentally still-frame oriented.
  const canUseBatchCapture = totalDiceNeeded >= 2 && totalDiceNeeded <= 6 && !requiresVideo;

  // TICKET-299: Roll settings drawer open state. Drawer is mounted always; user opens
  // it via the bottom-center handle (portrait) or right-edge handle (landscape).
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

  // Swipe-to-open the settings drawer (matches PracticePage pattern).
  const swipeDirection = isLandscape ? 'left' : 'up';
  const settingsSwipeHandlers = useSwipeGesture({
    direction: swipeDirection,
    threshold: 50,
    maxDuration: 500,
    onSwipe: () => {
      console.info(`${SERVICE_TAG} Settings drawer opened via swipe gesture`, {
        direction: swipeDirection
      });
      setSettingsDrawerOpen(true);
    }
  });

  // Reset capture state when roll request changes
  // This prevents stale state from previous requests (fixes wrong dice type label bug)
  useEffect(() => {
    setCapturedDice([]);
    setBatchResult(null);
    setCurrentDieIndex(0);
    setRollState('waiting');
    setError(null);

    // Abort any in-flight video recording from a prior request (TICKET-290).
    // Guard avoids a no-op abort call on initial mount.
    if (videoCapture.state !== 'idle') {
      videoCapture.abort();
    }

    if (pendingRollRequest) {
      console.info(`${SERVICE_TAG} New roll request received, state reset`, {
        requestId: pendingRollRequest.requestId,
        dice: pendingRollRequest.dice.map(d => `${d.count}${d.type}`).join(', ')
      });
    }
  }, [pendingRollRequest?.requestId]);

  // Top-level unmount cleanup: abort hook on navigation away (TICKET-290).
  useEffect(() => {
    return () => {
      videoCapture.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start sequential capture mode
  const handleStartSequential = useCallback(() => {
    if (!pendingRollRequest) return;

    const diceToCapture: CapturedDie[] = [];
    for (const diceSpec of pendingRollRequest.dice) {
      for (let i = 0; i < diceSpec.count; i++) {
        diceToCapture.push({
          type: diceSpec.type,
          imageData: '',
          captureKind: 'image',
          result: null,
          isProcessing: false
        });
      }
    }

    setCapturedDice(diceToCapture);
    setCurrentDieIndex(0);
    setCaptureMode('sequential');
    setRollState('ready');
    setError(null);
    setBatchResult(null);

    console.info(`${SERVICE_TAG} Starting sequential capture`, {
      totalDice: diceToCapture.length,
      request: pendingRollRequest.requestId
    });
  }, [pendingRollRequest]);

  // Start batch capture mode
  const handleStartBatch = useCallback(() => {
    if (!pendingRollRequest) return;

    setCaptureMode('batch');
    setRollState('ready');
    setError(null);
    setBatchResult(null);
    setCapturedDice([]);

    console.info(`${SERVICE_TAG} Starting batch capture`, {
      totalDice: totalDiceNeeded,
      request: pendingRollRequest.requestId
    });
  }, [pendingRollRequest, totalDiceNeeded]);

  // Capture single die (sequential mode)
  const handleCaptureSequential = useCallback(async () => {
    if (!cameraRef.current || currentDieIndex >= capturedDice.length) return;

    const imageData = cameraRef.current.capture();
    if (!imageData) {
      setError('Failed to capture image');
      return;
    }

    const currentDie = capturedDice[currentDieIndex];
    setRollState('capturing');
    setError(null);

    setCapturedDice(prev => {
      const updated = [...prev];
      updated[currentDieIndex] = {
        ...updated[currentDieIndex],
        imageData,
        captureKind: 'image',
        videoBlob: undefined,
        isProcessing: true
      };
      return updated;
    });

    try {
      const result = await recognizeDice(imageData, currentDie.type, { model: aiModel });
      setCapturedDice(prev => {
        const updated = [...prev];
        updated[currentDieIndex] = { ...updated[currentDieIndex], result, isProcessing: false };
        return updated;
      });
      setRollState('reviewing');
    } catch (err) {
      console.error(`${SERVICE_TAG} Recognition failed`, err);
      setError(err instanceof Error ? err.message : 'Recognition failed');
      setRollState('ready');
      setCapturedDice(prev => {
        const updated = [...prev];
        updated[currentDieIndex] = { ...updated[currentDieIndex], imageData: '', result: null, isProcessing: false };
        return updated;
      });
    }
  }, [capturedDice, currentDieIndex, aiModel]);

  /**
   * Begin a motion-driven video roll capture (TICKET-290). The hook owns the
   * recorder + motion detector. Recognition runs in an effect once the hook
   * delivers a finished recording.
   */
  const handleStartVideoCapture = useCallback(() => {
    if (!cameraRef.current || currentDieIndex >= capturedDice.length) return;
    const stream = cameraRef.current.getMediaStream();
    const videoEl = cameraRef.current.getVideoElement();
    if (!stream || !videoEl) {
      setError('Camera not ready');
      return;
    }
    setError(null);
    setRollState('capturing');
    videoCapture.start(stream, videoEl);
  }, [capturedDice.length, currentDieIndex, videoCapture]);

  // Run recognition once the hook delivers a finished recording.
  // We gate by recording identity via a ref so we never process the same blob
  // twice — a previous version had `capturedDice` in deps and called
  // setCapturedDice inside, which produced an infinite re-entry loop on the
  // recognition API when it errored.
  const processedRecordingRef = useRef<RollVideoRecording | null>(null);
  const capturedDiceRef = useRef(capturedDice);
  capturedDiceRef.current = capturedDice;
  const aiModelRef = useRef(aiModel);
  aiModelRef.current = aiModel;
  const currentDieIndexRef = useRef(currentDieIndex);
  currentDieIndexRef.current = currentDieIndex;
  useEffect(() => {
    if (videoCapture.state !== 'done' || !videoCapture.recording) return;
    if (processedRecordingRef.current === videoCapture.recording) return;
    processedRecordingRef.current = videoCapture.recording;

    const recordingBlob = videoCapture.recording.blob;
    const dieIndex = currentDieIndexRef.current;
    const dieType = capturedDiceRef.current[dieIndex]?.type;
    if (!dieType) return;
    let cancelled = false;

    const preFinalizedFrame = videoCapture.recording.preFinalizedFrame;

    (async () => {
      let firstFrame: string;
      // Prefer the live still grabbed at settle (TICKET-293) — it skips the
      // encoder quality tax that re-decoding the recorded blob incurs. Falls
      // through to extractFirstFrame on hard-cap (no settle) or capture failure.
      if (preFinalizedFrame) {
        firstFrame = preFinalizedFrame;
      } else {
        try {
          firstFrame = await extractFirstFrame(recordingBlob);
        } catch (err) {
          if (cancelled) return;
          console.error(`${SERVICE_TAG} Frame extraction failed`, err);
          setError('Could not extract frame for recognition');
          setRollState('ready');
          videoCapture.reset();
          return;
        }
      }
      if (cancelled) return;

      setCapturedDice(prev => {
        const updated = [...prev];
        updated[dieIndex] = {
          ...updated[dieIndex],
          imageData: firstFrame,
          videoBlob: recordingBlob,
          captureKind: 'video',
          isProcessing: true
        };
        return updated;
      });

      try {
        const result = await recognizeDice(firstFrame, dieType, { model: aiModelRef.current });
        if (cancelled) return;
        setCapturedDice(prev => {
          const updated = [...prev];
          updated[dieIndex] = { ...updated[dieIndex], result, isProcessing: false };
          return updated;
        });
        setRollState('reviewing');
      } catch (err) {
        if (cancelled) return;
        console.error(`${SERVICE_TAG} Recognition (video) failed`, err);
        setError(err instanceof Error ? err.message : 'Recognition failed');
        setRollState('ready');
        setCapturedDice(prev => {
          const updated = [...prev];
          updated[dieIndex] = { ...updated[dieIndex], isProcessing: false };
          return updated;
        });
      } finally {
        if (!cancelled) videoCapture.reset();
      }
    })();

    return () => {
      cancelled = true;
    };
    // videoCapture.reset is stable (useCallback inside the hook); other reads use refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoCapture.state, videoCapture.recording]);

  // Surface hook errors to the page error banner.
  useEffect(() => {
    if (videoCapture.state === 'error') {
      const code = videoCapture.errorCode;
      if (code === 'OVERSIZE') {
        toast.warning('Video too large after retry — please use image capture');
      } else if (code !== 'ABORTED') {
        setError(videoCapture.errorMessage ?? 'Recording failed');
      }
      setRollState('ready');
      videoCapture.reset();
    }
  }, [videoCapture.state, videoCapture.errorCode, videoCapture.errorMessage, videoCapture]);

  // Timeout: stay on camera, show retry banner via UI block below.
  useEffect(() => {
    if (videoCapture.state === 'timeout-no-motion') {
      setRollState('ready');
    }
  }, [videoCapture.state]);

  // Capture all dice at once (batch mode)
  const handleCaptureBatch = useCallback(async () => {
    if (!cameraRef.current || !pendingRollRequest) return;

    const imageData = cameraRef.current.capture();
    if (!imageData) {
      setError('Failed to capture image');
      return;
    }

    setRollState('capturing');
    setError(null);

    try {
      const expectedDice: DiceSpec[] = pendingRollRequest.dice.map(d => ({
        type: d.type,
        count: d.count
      }));

      const result = await recognizeMultipleDice(imageData, expectedDice, { model: aiModel });

      setBatchResult({
        dice: result.dice.map(d => ({ ...d, wasEdited: false })),
        imageData,
        warnings: result.warnings
      });
      setRollState('reviewing');

      console.info(`${SERVICE_TAG} Batch capture complete`, {
        expected: totalDiceNeeded,
        recognized: result.recognized
      });
    } catch (err) {
      console.error(`${SERVICE_TAG} Batch recognition failed`, err);
      setError(err instanceof Error ? err.message : 'Batch recognition failed');
      setRollState('ready');
    }
  }, [pendingRollRequest, totalDiceNeeded, aiModel]);

  // Handle value edit for sequential mode
  const handleSequentialEdit = useCallback((newValue: number) => {
    setCapturedDice(prev => {
      const updated = [...prev];
      if (updated[currentDieIndex]?.result) {
        const die = updated[currentDieIndex];
        updated[currentDieIndex] = {
          ...die,
          result: { ...die.result!, value: newValue },
          wasEdited: true,
          // Immutable: only capture the original value on the very first edit
          originalValue: die.originalValue ?? die.result!.value
        };
      }
      return updated;
    });
  }, [currentDieIndex]);

  // Handle value edit for batch mode
  const handleBatchEdit = useCallback((index: number, newValue: number) => {
    setBatchResult(prev => {
      if (!prev) return prev;
      const updatedDice = [...prev.dice];
      const die = updatedDice[index];
      updatedDice[index] = {
        ...die,
        value: newValue,
        wasEdited: true,
        // Immutable: only capture the original value on the very first edit
        originalValue: die.originalValue ?? die.value
      };
      return { ...prev, dice: updatedDice };
    });
  }, []);

  // Accept current die and move to next (sequential)
  const handleAcceptDie = useCallback(() => {
    if (currentDieIndex < capturedDice.length - 1) {
      setCurrentDieIndex(prev => prev + 1);
      setRollState('ready');
    } else {
      setRollState('reviewing');
    }
  }, [currentDieIndex, capturedDice.length]);

  // Retake current die (sequential)
  const handleRetakeSequential = useCallback(() => {
    setCapturedDice(prev => {
      const updated = [...prev];
      updated[currentDieIndex] = { ...updated[currentDieIndex], imageData: '', result: null, isProcessing: false };
      return updated;
    });
    setRollState('ready');
  }, [currentDieIndex]);

  // Retake batch capture
  const handleRetakeBatch = useCallback(() => {
    setBatchResult(null);
    setRollState('ready');
  }, []);

  // Submit results
  const handleSubmit = useCallback(async () => {
    if (!pendingRollRequest || !playerId || !selectedEndpointId) return;
    setRollState('submitting');

    let results;
    if (captureMode === 'batch' && batchResult) {
      results = batchResult.dice.map((die, index) => {
        let dieType = die.diceType;
        if (!dieType) {
          console.warn(`${SERVICE_TAG} Missing diceType in batch result, using fallback`, {
            fallbackValue: 'unknown',
            dieIndex: index
          });
          dieType = 'unknown';
        }
        return {
          type: dieType,
          value: die.value,
          wasEdited: die.wasEdited || false,
          confidence: die.confidence,
          originalValue: die.wasEdited ? die.originalValue : undefined
        };
      });
    } else {
      results = capturedDice.map(die => ({
        type: die.type,
        value: die.result?.value || 0,
        wasEdited: die.wasEdited || false,
        confidence: die.result?.confidence || 0,
        originalValue: die.wasEdited ? die.originalValue : undefined
      }));
    }

    console.info(`${SERVICE_TAG} Submitting results to endpoint`, {
      requestId: pendingRollRequest.requestId,
      endpointId: selectedEndpointId,
      results
    });

    // Get client for selected endpoint and send result
    const client = connectionManager.getClient(selectedEndpointId || '');
    if (!client) {
      console.error(`${SERVICE_TAG} No active connection found for endpoint`, {
        endpointId: selectedEndpointId
      });
      setRollState('reviewing');
      setError('No active connection found. Please reconnect and try again.');
      return;
    }

    // Extract image data once — used by both Discord (direct) and WebSocket (queued) paths
    // Batch mode: full frame with all dice. Sequential: last captured die image.
    const imageData = captureMode === 'batch' && batchResult
      ? batchResult.imageData
      : [...capturedDice].reverse().find(d => d.imageData)?.imageData;

    // TICKET-281c §3.11 — only one S3 video key per requestId. In sequential mode
    // we upload the LAST recorded video (most recent / proof of this roll).
    // Batch mode never produces a video.
    const lastVideoDie = captureMode === 'sequential'
      ? [...capturedDice].reverse().find(d => d.videoBlob)
      : undefined;
    const videoBlob = lastVideoDie?.videoBlob;
    const hasVideo = !!videoBlob;

    // For Discord endpoints, pass the captured media directly so the backend
    // attaches it as a file. Discord renders images inline in the embed and
    // videos as a playable card below the embed. TICKET-281f: when the user
    // recorded a video, send it as videoData (preferred over the still frame).
    if (client instanceof DiscordClient) {
      let videoData: string | undefined;
      let videoContentType: 'video/webm' | 'video/mp4' | undefined;
      if (hasVideo && videoBlob) {
        try {
          videoData = await blobToBase64(videoBlob);
          videoContentType = videoBlob.type.startsWith('video/mp4') ? 'video/mp4' : 'video/webm';
        } catch (err) {
          console.warn(`${SERVICE_TAG} Failed to encode video for Discord, falling back to image`, err);
        }
      }
      try {
        await client.sendRollResult(
          { requestId: pendingRollRequest.requestId, playerId, results },
          videoData
            ? { videoData, contentType: videoContentType }
            : { imageData }
        );
      } catch (err) {
        console.error(`${SERVICE_TAG} Discord send failed`, err);
        toast.error('Connection lost — please reconnect and try again');
        setRollState('reviewing');
        return;
      }
    } else if (proofMode === 'none') {
      // Proof not required — submit results without uploading any media (TICKET-281c §3.5).
      const sent = client.sendRollResult({
        type: 'rollResult',
        requestId: pendingRollRequest.requestId,
        playerId,
        results
      });
      if (!sent) {
        toast.error('Connection lost — please reconnect and try again');
        setRollState('reviewing');
        return;
      }
    } else {
      // WebSocket path: upload media to S3 first (best-effort), include presigned URL.
      // This bypasses the 128KB API Gateway WebSocket frame limit.
      let imageUrl: string | undefined;
      let videoUrl: string | undefined;

      if (hasVideo && videoBlob) {
        const uploadedUrl = await diceRegistryService.uploadRollVideo(
          pendingRollRequest.requestId,
          videoBlob
        );
        if (uploadedUrl) videoUrl = uploadedUrl;
      } else if (imageData) {
        const uploadedUrl = await diceRegistryService.uploadRollImage(
          pendingRollRequest.requestId,
          imageData
        );
        if (uploadedUrl) imageUrl = uploadedUrl;
      }

      const sent = client.sendRollResult({
        type: 'rollResult',
        requestId: pendingRollRequest.requestId,
        playerId,
        results,
        ...(imageUrl ? { imageUrl } : {}),
        ...(videoUrl ? { videoUrl } : {})
      });
      if (!sent) {
        toast.error('Connection lost — please reconnect and try again');
        setRollState('reviewing');
        return;
      }
    }

    setPendingRollRequest(null);
    setCapturedDice([]);
    setBatchResult(null);
    setCurrentDieIndex(0);
    setRollState('waiting');
  }, [pendingRollRequest, playerId, selectedEndpointId, capturedDice, batchResult, captureMode, proofMode, setPendingRollRequest]);

  // Cancel roll
  const handleCancel = useCallback(() => {
    // TICKET-289: Notify the requesting platform that the roll was cancelled.
    // Only send when there is a real pending roll request from a remote
    // platform — the Cancel button is also reused mid-capture flows where
    // there is no upstream request to notify.
    if (pendingRollRequest?.requestId && playerId && selectedEndpointId) {
      const client = connectionManager.getClient(selectedEndpointId);
      if (client instanceof WebSocketClient) {
        client.sendRollCancellation(
          pendingRollRequest.requestId,
          playerId,
          sessionCode
        );
      }
    }

    // Abort any active video recording (TICKET-290).
    videoCapture.abort();

    setPendingRollRequest(null);
    setCapturedDice([]);
    setBatchResult(null);
    setCurrentDieIndex(0);
    setRollState('waiting');
    setError(null);
  }, [setPendingRollRequest, pendingRollRequest, playerId, selectedEndpointId, sessionCode, videoCapture]);

  // Disconnected state
  if (!isConnected) {
    return (
      <div className={cn(
        'flex h-full items-center justify-center p-4 text-center',
        isLandscape ? 'flex-row gap-6' : 'flex-col'
      )}>
        <FrogMascot variant="disconnected" size="2xl" className={cn(
          isLandscape ? '' : 'mb-4'
        )} />
        <div>
          <h1 className={cn('font-bold', isLandscape ? 'text-lg mb-1' : 'text-xl mb-2')}>
            Disconnected
          </h1>
          <p className={cn('text-muted-foreground', isLandscape && 'text-sm')}>
            Go to Sessions to connect to your game session
          </p>
        </div>
      </div>
    );
  }

  // Awaiting request state
  if (!hasRequest) {
    return (
      <div className={cn(
        'flex h-full items-center justify-center p-4 text-center',
        isLandscape ? 'flex-row gap-6' : 'flex-col'
      )}>
        <FrogMascot variant="waiting" size="2xl" className={cn(
          isLandscape ? '' : 'mb-4'
        )} />
        <div>
          <h1 className={cn('font-bold text-glow-subtle', isLandscape ? 'text-lg mb-1' : 'text-xl mb-2')}>
            Waiting for a roll...
          </h1>
          <p className={cn('text-muted-foreground', isLandscape && 'text-sm')}>
            Your Game Master will send a dice roll request to your phone
          </p>
          <Badge variant="success" className="mt-4">Connected</Badge>
        </div>
      </div>
    );
  }

  // Roll request received - choose capture mode
  if (rollState === 'waiting') {
    return (
      <div className={cn(
        'flex h-full p-4 min-h-0',
        isLandscape ? 'flex-row gap-4' : 'flex-col'
      )}>
        <Card variant="elevated" className={cn(isLandscape ? 'flex-1' : 'mb-4')}>
          <CardHeader className={isLandscape ? 'py-3' : undefined}>
            <CardTitle className="flex items-center justify-between">
              <span className="text-glow-subtle">Roll Request</span>
              <Button variant="ghost" size="icon" onClick={handleCancel}><X className="h-4 w-4" /></Button>
            </CardTitle>
          </CardHeader>
          <CardContent className={isLandscape ? 'py-2' : undefined}>
            <p className={cn('text-muted-foreground', isLandscape ? 'mb-2 text-sm' : 'mb-4')}>
              {pendingRollRequest.context}
            </p>
            {pendingRollRequest.flavor && (
              <p className={cn('italic text-primary/80', isLandscape ? 'mb-2 text-xs' : 'mb-4 text-sm')}>
                {pendingRollRequest.flavor}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {pendingRollRequest.dice.map((spec, i) => (
                <Badge key={i} variant="accent" className={cn(isLandscape ? 'text-base px-2 py-0.5' : 'text-lg px-3 py-1')}>
                  {spec.count}{spec.type.toUpperCase()}
                </Badge>
              ))}
            </div>

            <PwaInstallNudge createdAt={pendingRollRequest.createdAt} />

            {/* iOS warning when video proof is required/allowed (TICKET-281c) */}
            <IosVideoWarning proofMode={proofMode} />

            {/* Endpoint selector (multi-connection support) */}
            {connectedEndpoints.length > 1 && (
              <div className={cn('space-y-2', isLandscape ? 'mt-3' : 'mt-4')}>
                <label
                  htmlFor="endpoint-selector"
                  className={cn('text-sm font-medium text-muted-foreground', isLandscape && 'text-xs')}
                >
                  <Wifi className="inline h-3 w-3 mr-1" aria-hidden="true" />
                  Send to:
                </label>
                <Select value={selectedEndpointId || undefined} onValueChange={setSelectedEndpointId}>
                  <SelectTrigger
                    id="endpoint-selector"
                    className={cn(isLandscape && 'text-sm h-8')}
                    aria-label="Select connection for sending roll results"
                  >
                    <SelectValue placeholder="Select connection" />
                  </SelectTrigger>
                  <SelectContent>
                    {connectedEndpoints.map(ep => (
                      <SelectItem
                        key={ep.id}
                        value={ep.id}
                        aria-label={`${ep.name} - ${ep.type} connection`}
                      >
                        <div className="flex items-center gap-2">
                          <EndpointTypeIcon type={ep.type} size={16} aria-hidden="true" />
                          <span>{ep.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {!isLandscape && <div className="flex-1" />}

        <div className={cn(
          'space-y-2',
          isLandscape && 'w-[200px] flex-shrink-0 flex flex-col justify-center'
        )}>
          <Button
            variant="accent"
            size={isLandscape ? 'default' : 'lg'}
            className={cn('w-full', !isLandscape && 'py-6 text-lg')}
            onClick={handleStartSequential}
            enablePressEffect
          >
            <Camera className="mr-2 h-5 w-5" />
            {isLandscape ? `One by One (${totalDiceNeeded})` : `Capture One by One (${totalDiceNeeded} ${totalDiceNeeded === 1 ? 'die' : 'dice'})`}
          </Button>
          {canUseBatchCapture && (
            <Button
              variant="secondary-action"
              size={isLandscape ? 'default' : 'lg'}
              className={cn('w-full', !isLandscape && 'py-5')}
              onClick={handleStartBatch}
              enablePressEffect
            >
              <Layers className="mr-2 h-5 w-5" />
              {isLandscape ? 'Capture All' : 'Capture All at Once'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Batch capture mode - TICKET-126: Camera-first with floating button
  if (captureMode === 'batch') {
    if (rollState === 'reviewing' && batchResult) {
      return (
        <PhotoHeroBatchResult
          imageData={batchResult.imageData}
          dice={batchResult.dice}
          expectedCount={totalDiceNeeded}
          onDieEdit={handleBatchEdit}
          warnings={batchResult.warnings}
          actions={
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  size="lg"
                  variant="secondary-action"
                  className="flex-1 py-5"
                  onClick={handleRetakeBatch}
                  enablePressEffect
                >
                  <RotateCcw className="mr-2 h-5 w-5" />Retake
                </Button>
                <Button
                  variant="accent"
                  size="lg"
                  className="flex-1 py-5"
                  onClick={handleSubmit}
                  enablePressEffect
                >
                  <Send className="mr-2 h-5 w-5" />Send
                </Button>
              </div>
              <Button
                variant="ghost"
                className="w-full text-white/60 hover:text-red-400"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          }
        />
      );
    }

    // Camera view - floating button layout
    return (
      <div className="relative h-full w-full">
        {/* Camera takes full container */}
        <div className="absolute inset-0 m-2">
          <CameraView
            ref={cameraRef}
            className="h-full w-full rounded-lg"
            onError={handleCameraError}
            onTorchStateChange={setTorchState}
          />
        </div>

        {/* Unified top bar for camera controls */}
        <CameraTopBar
          left={
            torchState.supported ? (
              <TorchButton
                enabled={torchState.enabled}
                onToggle={() => cameraRef.current?.toggleTorch()}
              />
            ) : null
          }
          center={
            <Badge variant="accent" className="backdrop-blur-sm bg-primary/80">
              Batch: {totalDiceNeeded} dice
            </Badge>
          }
          centerSecondary={
            <>
              {error && (
                <div className="max-w-[90vw] rounded-md bg-destructive/90 p-3 text-sm text-destructive-foreground backdrop-blur-sm border border-destructive/30">
                  {error}
                </div>
              )}
              {rollState === 'capturing' && (
                <>
                  <FrogWaitAnimation />
                  <AiRecognitionIndicator />
                </>
              )}
            </>
          }
        />

        {/* Floating capture button */}
        {rollState !== 'submitting' && (
          <div className={cn(
            'absolute left-1/2 -translate-x-1/2 z-10',
            isLandscape ? 'bottom-4' : 'bottom-16'
          )}>
            <FloatingCaptureButton
              mode="manual"
              autoState="paused"
              // Batch capture is photo-only (requiresVideo is filtered out upstream
              // via canUseBatchCapture). Force the camera icon — the user-level
              // capture-mode toggle does not apply here.
              captureKind="photo"
              onCapture={handleCaptureBatch}
              onEnterAutoMode={() => {}}
              onExitAutoMode={() => {}}
              onPause={() => {}}
              onResume={() => {}}
              disabled={rollState === 'capturing'}
            />
          </div>
        )}

        {/* Cancel button overlay */}
        <div className="absolute bottom-4 right-4 z-10">
          <Button
            variant="ghost"
            size="sm"
            className="backdrop-blur-sm bg-card/80 hover:bg-card text-muted-foreground hover:text-destructive"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Sequential capture mode - TICKET-126: Camera-first with floating button
  const currentDie = capturedDice[currentDieIndex];
  const allDiceCaptured = capturedDice.every(d => d.result !== null);

  if (rollState === 'reviewing') {
    return (
      <PhotoHeroResult
        imageData={currentDie?.imageData || ''}
        kind={currentDie?.captureKind ?? 'image'}
        videoBlob={currentDie?.videoBlob}
        value={currentDie?.result?.value || 0}
        diceType={currentDie?.type || 'd20'}
        confidence={currentDie?.result?.confidence || 0}
        animationState="entering"
        onValueEdit={handleSequentialEdit}
        wasEdited={currentDie?.wasEdited}
        originalValue={currentDie?.originalValue}
        actions={
          <div className="space-y-2">
            {!allDiceCaptured ? (
              <div className="flex gap-2">
                <Button
                  size="lg"
                  variant="secondary-action"
                  className="flex-1 py-5"
                  onClick={handleRetakeSequential}
                  enablePressEffect
                >
                  <RotateCcw className="mr-2 h-5 w-5" />Retake
                </Button>
                <Button
                  variant="accent"
                  size="lg"
                  className="flex-1 py-5"
                  onClick={handleAcceptDie}
                  enablePressEffect
                >
                  <Check className="mr-2 h-5 w-5" />Next
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap justify-center gap-2 mb-2">
                  {capturedDice.map((die, i) => (
                    <span
                      key={i}
                      className="backdrop-blur-sm bg-white/10 rounded-full px-3 py-1 text-sm text-white border border-white/10"
                    >
                      {die.type.toUpperCase()}: {die.result?.value}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="lg"
                    variant="secondary-action"
                    className="flex-1 py-5"
                    onClick={handleRetakeSequential}
                    enablePressEffect
                  >
                    <RotateCcw className="mr-2 h-5 w-5" />Retake
                  </Button>
                  <Button
                    variant="accent"
                    size="lg"
                    className="flex-1 py-5"
                    onClick={handleSubmit}
                    enablePressEffect
                  >
                    <Send className="mr-2 h-5 w-5" />Send
                  </Button>
                </div>
              </>
            )}
            <Button
              variant="ghost"
              className="w-full text-white/60 hover:text-red-400"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        }
      />
    );
  }

  // Camera view - floating button layout
  return (
    <div className="relative h-full w-full">
      {/* Camera takes full container */}
      <div className="absolute inset-0 m-2">
        <CameraView
          ref={cameraRef}
          className="h-full w-full rounded-lg"
          onError={handleCameraError}
          onTorchStateChange={setTorchState}
        />
      </div>

      {/* Unified top bar for camera controls */}
      <CameraTopBar
        left={
          torchState.supported ? (
            <TorchButton
              enabled={torchState.enabled}
              onToggle={() => cameraRef.current?.toggleTorch()}
            />
          ) : null
        }
        center={
          <Badge variant="accent" className="backdrop-blur-sm bg-primary/80">
            {currentDieIndex + 1} of {capturedDice.length}: {currentDie?.type?.toUpperCase()}
          </Badge>
        }
        centerSecondary={
          <>
            {error && (
              <div className="max-w-[90vw] rounded-md bg-destructive/90 p-3 text-sm text-destructive-foreground backdrop-blur-sm border border-destructive/30">
                {error}
              </div>
            )}
            {/* TICKET-290: motion-driven REC indicator */}
            {videoCapture.state === 'waiting-for-motion' && (
              <div
                className="rounded-full bg-red-600/80 px-4 py-2 text-white font-mono text-sm border border-white/20"
                aria-label="Waiting for roll"
              >
                <span className="inline-block h-2 w-2 mr-2 rounded-full bg-white animate-pulse" />
                Roll now! {videoCapture.secondsRemaining}s
              </div>
            )}
            {videoCapture.state === 'rolling' && (
              <div
                className="rounded-full bg-red-600/80 px-4 py-2 text-white font-mono text-sm border border-white/20 animate-pulse"
                aria-label="Recording in progress"
              >
                <span className="inline-block h-2 w-2 mr-2 rounded-full bg-white" />
                Rolling…
              </div>
            )}
            {videoCapture.state === 'finalizing' && (
              <div className="rounded-full bg-card/80 px-4 py-2 text-foreground text-sm">
                Processing…
              </div>
            )}
            {videoCapture.state === 'timeout-no-motion' && (
              <div className="max-w-[90vw] rounded-md bg-destructive/90 p-3 text-sm text-destructive-foreground backdrop-blur-sm border border-destructive/30 flex items-center gap-3">
                <span>No roll detected — try again</span>
                <Button
                  size="sm"
                  variant="accent"
                  onClick={() => {
                    videoCapture.reset();
                    handleStartVideoCapture();
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
            {((rollState === 'capturing' && videoCapture.state === 'idle') || currentDie?.isProcessing) && (
              <>
                <FrogWaitAnimation />
                <AiRecognitionIndicator
                  current={currentDieIndex + 1}
                  total={capturedDice.length}
                />
              </>
            )}
          </>
        }
      />

      {/* Floating capture button */}
      {rollState !== 'submitting' && (
        <div className={cn(
          'absolute left-1/2 -translate-x-1/2 z-10',
          isLandscape ? 'bottom-4' : 'bottom-16'
        )}>
          <FloatingCaptureButton
            mode="manual"
            autoState="paused"
            // TICKET-281c proofMode wiring + TICKET-299 capture-mode preference:
            //   lockedMode set (image/video proof) overrides preference.
            //   Otherwise, persisted captureMode chooses tap behaviour.
            // Long-press still toggles to video as a power-user shortcut when allowed.
            captureKind={effectiveCaptureMode}
            onCapture={effectiveCaptureMode === 'video' ? handleStartVideoCapture : handleCaptureSequential}
            onEnterAutoMode={canVideo && !requiresVideo ? handleStartVideoCapture : () => {}}
            onExitAutoMode={() => {}}
            onPause={() => {}}
            onResume={() => {}}
            disabled={rollState === 'capturing' || currentDie?.isProcessing}
          />
        </div>
      )}

      {/* Cancel button overlay */}
      <div className="absolute bottom-4 right-4 z-10">
        <Button
          variant="ghost"
          size="sm"
          className="backdrop-blur-sm bg-card/80 hover:bg-card text-muted-foreground hover:text-destructive"
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>

      {/* TICKET-299: Roll settings drawer (capture-mode toggle). */}
      <RollSettingsDrawer
        open={settingsDrawerOpen}
        onOpenChange={setSettingsDrawerOpen}
        captureMode={persistedCaptureMode}
        onCaptureModeChange={(mode) => updateSettings({ captureMode: mode })}
        lockedMode={lockedMode}
      />

      {/* Drawer handle trigger - click or swipe to open */}
      <button
        type="button"
        onClick={() => setSettingsDrawerOpen(true)}
        {...settingsSwipeHandlers}
        className={cn(
          'absolute z-10 flex items-center justify-center',
          'bg-card/80 backdrop-blur-sm border border-primary/30 rounded-t-lg',
          'transition-colors hover:bg-card',
          isLandscape
            ? 'right-0 top-1/2 -translate-y-1/2 w-6 h-16 rounded-l-lg rounded-tr-none'
            : 'bottom-0 left-1/2 -translate-x-1/2 w-32 h-6'
        )}
        aria-label="Open settings. Click or swipe to open."
        data-testid="roll-drawer-handle"
      >
        <div className={cn(
          'bg-muted rounded-full',
          isLandscape ? 'w-1 h-8' : 'w-12 h-1'
        )} />
      </button>
    </div>
  );
}
